import { ENV } from '../../config/runtimeEnv';
import {
	SampleResult,
	SampleResultStatistics,
	UploadResultRequest,
	UploadResultResponse,
	ResultFilter,
	PaginatedResultResponse,
	AnalyzeResultResponse
} from "../../types/lab/SampleResultTypes";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_LABIQ_SERVER;

// Get all results with filtering and pagination
export const fetchResults = async (
	filters: ResultFilter = {}
): Promise<PaginatedResultResponse> => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params.append(key, value.toString());
		}
	});

	const response = await http.get(`${API_BASE_URL}/results?${params.toString()}`);

	return response.data;
};

// Get a specific result by ID
export const fetchResult = async (id: number): Promise<SampleResult> => {
	const response = await http.get(`${API_BASE_URL}/results/${id}`);

	return response.data;
};

// Delete a result
export const deleteResult = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/results/${id}`);
};

// Get result statistics
export const fetchResultStatistics = async (filters: {
	date_from?: string;
	date_to?: string;
} = {}): Promise<SampleResultStatistics> => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params.append(key, value.toString());
		}
	});

	const response = await http.get(
		`${API_BASE_URL}/results/statistics/overview?${params.toString()}`
	);

	return response.data;
};

// Upload result for individual sample processing (first protocol)
export const uploadIndividualSampleResult = async (
	instanceId: number,
	request: UploadResultRequest,
	onProgress?: (progress: number) => void
): Promise<UploadResultResponse> => {
	console.log('Uploading sample result:', { instanceId, request });

	if (request.result_type !== 'manual_entry' && request.files && request.files.length > 0) {
		const formData = new FormData();
		formData.append('result_type', request.result_type);
		formData.append('uploaded_by', request.uploaded_by.toString());

		if (request.notes) {
			formData.append('notes', request.notes);
		}

		if (request.auto_analyze !== undefined) {
			formData.append('auto_analyze', request.auto_analyze ? '1' : '0');
		}

		if (request.data && Object.keys(request.data).length > 0) {
			formData.append('data', JSON.stringify(request.data));
		}

		request.files.forEach((file, index) => {
			formData.append(`files[${index}]`, file);
		});

		const response = await http.post(
			`${API_BASE_URL}/results/individual/sample/${instanceId}/upload`,
			formData,
			{
				timeout: 0,
				maxContentLength: Infinity,
				maxBodyLength: Infinity,
				onUploadProgress: (progressEvent) => {
					if (onProgress && progressEvent.total) {
						const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
						onProgress(progress);
					}
				},
			}
		);

		return response.data;
	} else {
		const response = await http.post(
			`${API_BASE_URL}/results/individual/sample/${instanceId}/upload`,
			{
				result_type: request.result_type,
				data: request.data,
				notes: request.notes,
				uploaded_by: request.uploaded_by,
				auto_analyze: request.auto_analyze
			},
			{
				headers: {
					'Content-Type': 'application/json',
				},
			}
		);

		return response.data;
	}
};

// Upload result for individual analysis processing (non-first protocol)
export const uploadIndividualAnalysisResult = async (
	instanceId: number,
	request: UploadResultRequest,
	onProgress?: (progress: number) => void
): Promise<UploadResultResponse> => {
	console.log('Uploading analysis result:', { instanceId, request });

	if (request.result_type !== 'manual_entry' && request.files && request.files.length > 0) {
		const formData = new FormData();
		formData.append('result_type', request.result_type);
		formData.append('uploaded_by', request.uploaded_by.toString());

		if (request.notes) {
			formData.append('notes', request.notes);
		}

		if (request.auto_analyze !== undefined) {
			formData.append('auto_analyze', request.auto_analyze ? '1' : '0');
		}

		if (request.data && Object.keys(request.data).length > 0) {
			formData.append('data', JSON.stringify(request.data));
		}

		request.files.forEach((file, index) => {
			formData.append(`files[${index}]`, file);
		});

		const response = await http.post(
			`${API_BASE_URL}/results/individual/analysis/${instanceId}/upload`,
			formData,
			{
				timeout: 0,
				maxContentLength: Infinity,
				maxBodyLength: Infinity,
				onUploadProgress: (progressEvent) => {
					if (onProgress && progressEvent.total) {
						const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
						onProgress(progress);
					}
				},
			}
		);

		return response.data;
	} else {
		const response = await http.post(
			`${API_BASE_URL}/results/individual/analysis/${instanceId}/upload`,
			{
				result_type: request.result_type,
				data: request.data,
				notes: request.notes,
				uploaded_by: request.uploaded_by,
				auto_analyze: request.auto_analyze
			},
			{
				headers: {
					'Content-Type': 'application/json',
				},
			}
		);

		return response.data;
	}
};

// Legacy: Upload result for individual processing (auto-detects sample vs analysis)
export const uploadIndividualResult = async (
	instanceId: number,
	request: UploadResultRequest,
	onProgress?: (progress: number) => void
): Promise<UploadResultResponse> => {
	console.log('Uploading individual result (legacy auto-detect):', { instanceId, request });

	if (request.result_type !== 'manual_entry' && request.files && request.files.length > 0) {
		const formData = new FormData();
		formData.append('result_type', request.result_type);
		formData.append('uploaded_by', request.uploaded_by.toString());

		if (request.notes) {
			formData.append('notes', request.notes);
		}

		if (request.auto_analyze !== undefined) {
			formData.append('auto_analyze', request.auto_analyze ? '1' : '0');
		}

		if (request.data && Object.keys(request.data).length > 0) {
			formData.append('data', JSON.stringify(request.data));
		}

		request.files.forEach((file, index) => {
			formData.append(`files[${index}]`, file);
		});

		const response = await http.post(
			`${API_BASE_URL}/results/individual/${instanceId}/upload`,
			formData,
			{
				timeout: 0,
				maxContentLength: Infinity,
				maxBodyLength: Infinity,
				onUploadProgress: (progressEvent) => {
					if (onProgress && progressEvent.total) {
						const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
						onProgress(progress);
					}
				},
			}
		);

		return response.data;
	} else {
		const response = await http.post(
			`${API_BASE_URL}/results/individual/${instanceId}/upload`,
			{
				result_type: request.result_type,
				data: request.data,
				notes: request.notes,
				uploaded_by: request.uploaded_by,
				auto_analyze: request.auto_analyze
			},
			{
				headers: {
					'Content-Type': 'application/json',
				},
			}
		);

		return response.data;
	}
};

// Get results for a specific individual sample protocol instance
export const fetchIndividualSampleResults = async (instanceId: number): Promise<SampleResult[]> => {
	const response = await http.get(`${API_BASE_URL}/results/individual/sample/${instanceId}`);

	return response.data;
};

// Get results for a specific individual analysis protocol instance
export const fetchIndividualAnalysisResults = async (instanceId: number): Promise<SampleResult[]> => {
	const response = await http.get(`${API_BASE_URL}/results/individual/analysis/${instanceId}`);

	return response.data;
};

// Legacy: Get results for individual instance (auto-detects sample vs analysis)
export const fetchIndividualResults = async (instanceId: number): Promise<SampleResult[]> => {
	const response = await http.get(`${API_BASE_URL}/results/individual/${instanceId}`);

	return response.data;
};

// Upload result for group processing (handles both sample and analysis groups)
export const uploadGroupResult = async (
	groupId: number,
	request: UploadResultRequest,
	onProgress?: (progress: number) => void
): Promise<UploadResultResponse> => {
	console.log('Starting group upload:', {
		groupId,
		resultType: request.result_type,
		fileCount: request.files?.length || 0,
		totalSize: request.files?.reduce((sum, f) => sum + f.size, 0) || 0
	});

	if (request.result_type !== 'manual_entry' && request.files && request.files.length > 0) {
		const formData = new FormData();

		formData.append('result_type', request.result_type);
		formData.append('uploaded_by', request.uploaded_by.toString());

		if (request.notes) {
			formData.append('notes', request.notes);
		}

		if (request.auto_analyze !== undefined) {
			formData.append('auto_analyze', request.auto_analyze ? '1' : '0');
		}

		if (request.data && Object.keys(request.data).length > 0) {
			formData.append('data', JSON.stringify(request.data));
		}

		request.files.forEach((file, index) => {
			formData.append(`files[${index}]`, file);
			console.log(`Added file ${index}:`, {
				name: file.name,
				size: file.size,
				type: file.type
			});
		});

		try {
			console.log('Sending upload request...');
			const startTime = Date.now();

			const response = await http.post(
				`${API_BASE_URL}/results/group/${groupId}/upload`,
				formData,
				{
					timeout: 0,
					maxContentLength: Infinity,
					maxBodyLength: Infinity,
					onUploadProgress: (progressEvent) => {
						if (onProgress && progressEvent.total) {
							const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
							const elapsed = (Date.now() - startTime) / 1000;
							const speed = progressEvent.loaded / elapsed / 1024 / 1024;

							console.log('Upload progress:', {
								progress: `${progress}%`,
								loaded: `${(progressEvent.loaded / 1024 / 1024).toFixed(2)} MB`,
								total: `${(progressEvent.total / 1024 / 1024).toFixed(2)} MB`,
								speed: `${speed.toFixed(2)} MB/s`,
								elapsed: `${elapsed.toFixed(1)}s`
							});

							onProgress(progress);
						}
					},
					validateStatus: (status) => {
						return status >= 200 && status < 300;
					}
				}
			);

			const duration = ((Date.now() - startTime) / 1000).toFixed(1);
			console.log(`Upload completed successfully in ${duration}s`);

			return response.data;
		} catch (error: any) {
			console.error('Upload error:', {
				message: error.message,
				code: error.code,
				response: error.response?.data,
				status: error.response?.status
			});

			if (error.code === 'ECONNABORTED') {
				throw new Error('Upload timeout - the file took too long to upload. Please check your connection and try again.');
			} else if (error.code === 'ERR_NETWORK') {
				throw new Error('Network error during upload. The file may have been uploaded but the response was lost. Please check the results list before retrying.');
			} else if (error.response) {
				const serverMessage = error.response.data?.message || error.response.statusText;
				throw new Error(`Server error (${error.response.status}): ${serverMessage}`);
			} else if (error.request) {
				throw new Error('No response from server. The upload may still be processing. Please check the results list.');
			} else {
				throw new Error(`Upload failed: ${error.message}`);
			}
		}
	} else {
		const response = await http.post(
			`${API_BASE_URL}/results/group/${groupId}/upload`,
			{
				result_type: request.result_type,
				data: request.data,
				notes: request.notes,
				uploaded_by: request.uploaded_by,
				auto_analyze: request.auto_analyze
			},
			{
				headers: {
					'Content-Type': 'application/json',
				},
			}
		);

		return response.data;
	}
};

// Get results for a specific processing group (handles both sample and analysis groups)
export const fetchGroupResults = async (groupId: number): Promise<SampleResult[]> => {
	const response = await http.get(`${API_BASE_URL}/results/group/${groupId}`);

	return response.data;
};

// Analyze a result
export const analyzeResult = async (id: number): Promise<AnalyzeResultResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/results/${id}/analyze`,
		{}
	);

	return response.data;
};

// Download a result file
export const downloadResultFile = async (
	resultId: number,
	fileIndex: number
): Promise<Blob> => {
	const response = await http.get(
		`${API_BASE_URL}/results/${resultId}/files/${fileIndex}/download`,
		{
			responseType: 'blob',
		}
	);

	return response.data;
};

// Helper function to create a download link for a file
export const downloadResultFileWithFilename = async (
	resultId: number,
	fileIndex: number,
	filename: string
): Promise<void> => {
	try {
		const blob = await downloadResultFile(resultId, fileIndex);

		const url = window.URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();

		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	} catch (error) {
		console.error('Error downloading file:', error);
		throw error;
	}
};